/**
 * Thin fetch wrapper for backend calls.
 *
 * - Always sends cookies (session is httpOnly so JS can't read it,
 *   but the browser sends it automatically with `credentials`).
 * - Throws `ApiError` on non-2xx so callers don't need to remember to
 *   check `res.ok`.
 * - Distinguishes 401 (logged-out, recoverable by login) from network
 *   errors (backend unreachable → caller may decide local-first mode).
 * - Prefixes paths with `VITE_API_BASE_URL` so the SPA can sit on a
 *   different origin from the API in production. In dev the env var
 *   is unset and the Vite proxy forwards `/api/*` to the backend.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? mensagemDoErro(status, body))
    this.status = status
    this.body = body
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

/** O texto que o servidor explicou (o `detail` do FastAPI, em PT), ou
 *  uma mensagem genérica com o código. Era sempre "API error 401", em
 *  inglês, e era isso que os módulos mostravam. */
export function mensagemDoErro(status: number, body: unknown): string {
  const detalhe = (body as { detail?: unknown } | null)?.detail
  if (typeof detalhe === 'string' && detalhe.trim()) return detalhe
  return `O servidor recusou o pedido (código ${status}).`
}

/** Evento que avisa a autenticação de que um pedido a meio do trabalho
 *  recebeu 401. Quem decide se a sessão acabou é o `AuthProvider`, que
 *  confirma com /api/auth/me: um 401 também pode ser, por exemplo, a
 *  chave do assistente recusada pelo fornecedor. */
export const EVENTO_API_401 = 'vn2r:api-401'

function avisarSe401(path: string, status: number): void {
  if (status !== 401 || path.startsWith('/api/auth/')) return
  try {
    window.dispatchEvent(new Event(EVENTO_API_401))
  } catch {
    /* sem window (testes em Node) */
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Network error')
    this.cause = cause
  }
}

export type RequestInitJson = Omit<RequestInit, 'body'> & {
  json?: unknown
}

async function request<T>(path: string, init: RequestInitJson = {}): Promise<T> {
  const { json, headers, ...rest } = init
  const finalHeaders = new Headers(headers)
  let body: BodyInit | undefined
  if (json !== undefined) {
    finalHeaders.set('Content-Type', 'application/json')
    body = JSON.stringify(json)
  }

  let res: Response
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers: finalHeaders,
      body,
    })
  } catch (err) {
    throw new NetworkError(err)
  }

  if (res.status === 204) {
    return undefined as T
  }

  const contentType = res.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')
  const payload: unknown = isJson ? await res.json() : await res.text()

  if (!res.ok) {
    avisarSe401(path, res.status)
    throw new ApiError(res.status, payload)
  }
  return payload as T
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: 'POST', json }),
  patch: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: 'PATCH', json }),
  put: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: 'PUT', json }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /**
   * Multipart POST. Skips the JSON body branch so the browser sets
   * Content-Type with the right boundary automatically.
   */
  postForm: async <T>(path: string, form: FormData): Promise<T> => {
    let res: Response
    try {
      res = await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      })
    } catch (err) {
      throw new NetworkError(err)
    }
    if (res.status === 204) return undefined as T
    const contentType = res.headers.get('content-type') ?? ''
    const isJson = contentType.includes('application/json')
    const payload: unknown = isJson ? await res.json() : await res.text()
    if (!res.ok) {
      avisarSe401(path, res.status)
      throw new ApiError(res.status, payload)
    }
    return payload as T
  },
}
